import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, Eye, EyeOff, Copy, ChevronUp, ChevronDown, Loader2,
  Pencil, X, Gift, GraduationCap, Layers,
} from "lucide-react";
import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/api";

const A = {
  bg: "#F7F5FB", card: "#FFFFFF", primary: "#5A3FC0", primarySoft: "#EDE7FF",
  text: "#2A1F4A", muted: "#7B7595", border: "#E8E1FA",
  danger: "#DC2A48", success: "#1A8A53", warn: "#D97706", gold: "#B8862F",
};

const inputCls = "w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-[#5A3FC0]/25";
const inputStyle = { borderColor: A.border, color: A.text, background: "white" };

function Badge({ children, kind = "muted" }) {
  const map = {
    success: { background: "#E7F6EE", color: A.success },
    warn: { background: "#FEF3E2", color: A.warn },
    gold: { background: "#FBF3D9", color: A.gold },
    muted: { background: "#F1EEF9", color: A.muted },
  };
  return (
    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
          style={map[kind]}>{children}</span>
  );
}

function ModuleForm({ initial, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(initial?.title_it || "");
  const [description, setDescription] = useState(initial?.description_it || "");
  const [coverImage, setCoverImage] = useState(initial?.cover_image_url || "");
  return (
    <div className="space-y-3 p-4 rounded-xl" style={{ background: "#FBFAFF", border: `1px solid ${A.border}` }}>
      <input value={title} onChange={(e) => setTitle(e.target.value)}
             data-testid="module-form-title"
             className={inputCls} style={inputStyle} placeholder="Titolo del modulo" />
      <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                data-testid="module-form-description" rows={2}
                className={inputCls} style={inputStyle} placeholder="Descrizione (opzionale)" />
      <input value={coverImage} onChange={(e) => setCoverImage(e.target.value)}
             data-testid="module-form-cover"
             className={inputCls} style={inputStyle} placeholder="URL immagine di copertina (opzionale)" />
      <div className="flex gap-2">
        <button onClick={() => onSave({ title, description, cover_image_url: coverImage })}
                disabled={saving || !title.trim()} data-testid="module-form-save"
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: A.primary }}>
          {saving ? "Salvataggio…" : "Salva modulo"}
        </button>
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${A.border}`, color: A.muted }}>
          Annulla
        </button>
      </div>
    </div>
  );
}

export default function VIPAdminContent() {
  const navigate = useNavigate();
  const [modules, setModules] = useState(null);
  const [lessons, setLessons] = useState(null);
  const [creatingModule, setCreatingModule] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleteModule, setDeleteModule] = useState(null);
  const [deleteLesson, setDeleteLesson] = useState(null);

  const load = () => {
    apiGet("/admin/vip/modules").then(setModules).catch((e) => toast.error(e?.response?.data?.detail || "Errore"));
    apiGet("/admin/vip/lessons").then(setLessons).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!modules || !lessons) {
    return (
      <div className="min-h-[40vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: A.primary }} />
      </div>
    );
  }

  const sorted = [...modules].sort((a, b) => (a.order || 0) - (b.order || 0));
  const legacyLessons = lessons.filter((l) => l.format !== "blocks");
  const lessonsFor = (mid) =>
    lessons.filter((l) => l.module_id === mid && l.format === "blocks")
      .sort((a, b) => (a.order || 0) - (b.order || 0));

  const purgeLegacy = async () => {
    setBusy(true);
    try {
      const r = await apiPost("/admin/vip/cms/purge-legacy", {});
      toast.success(`${r.deleted_lessons} vecchie lezioni eliminate definitivamente`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };

  const createModule = async (form) => {
    setBusy(true);
    try {
      await apiPost("/admin/vip/cms/modules", form);
      toast.success("Modulo creato");
      setCreatingModule(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const saveModule = async (mid, form) => {
    setBusy(true);
    try {
      await apiPatch(`/admin/vip/cms/modules/${mid}`, form);
      toast.success("Modulo aggiornato");
      setEditingModule(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const moveModule = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sorted.length) return;
    const ids = sorted.map((m) => m.module_id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try {
      await apiPost("/admin/vip/cms/modules/reorder", { module_ids: ids });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const confirmDeleteModule = async () => {
    setBusy(true);
    try {
      const r = await apiDelete(`/admin/vip/modules/${deleteModule.module_id}`);
      toast.success(`Modulo eliminato (${r.deleted_lessons || 0} lezioni rimosse)`);
      setDeleteModule(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };

  const createLesson = async (mid) => {
    setBusy(true);
    try {
      const doc = await apiPost("/admin/vip/cms/lessons", { module_id: mid, title: "Nuova lezione" });
      navigate(`/admin/vip/lessons/${doc.lesson_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const duplicateLesson = async (lid) => {
    try {
      const copy = await apiPost(`/admin/vip/cms/lessons/${lid}/duplicate`, {});
      toast.success("Lezione duplicata");
      navigate(`/admin/vip/lessons/${copy.lesson_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const togglePublish = async (l) => {
    try {
      await apiPost(`/admin/vip/lessons/${l.lesson_id}/publish?published=${l.published === false}`, {});
      toast.success(l.published === false ? "Lezione pubblicata" : "Lezione in bozza");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const toggleFree = async (l) => {
    try {
      await apiPost(`/admin/vip/lessons/${l.lesson_id}/free-preview`, { enabled: !l.is_free_preview });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const moveLesson = async (mid, idx, dir) => {
    const list = lessonsFor(mid);
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const ids = list.map((l) => l.lesson_id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try {
      await apiPost("/admin/vip/cms/lessons/reorder", { module_id: mid, lesson_ids: ids });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const confirmDeleteLesson = async () => {
    setBusy(true);
    try {
      await apiDelete(`/admin/vip/lessons/${deleteLesson.lesson_id}`);
      toast.success("Lezione eliminata");
      setDeleteLesson(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };

  return (
    <div data-testid="admin-content-manager" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl" style={{ color: A.text }}>
            Contenuti dell'Academia
          </h2>
          <p className="text-sm mt-0.5" style={{ color: A.muted }}>
            {modules.length} moduli · {lessons.filter((l) => l.format === "blocks").length} lezioni · creazione 100% manuale
          </p>
        </div>
        <button onClick={() => { setCreatingModule(true); setEditingModule(null); }}
                data-testid="create-module-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
                style={{ background: A.primary }}>
          <Layers className="h-4 w-4" /> Nuovo modulo
        </button>
      </div>

      {legacyLessons.length > 0 ? (
        <div data-testid="legacy-purge-banner"
             className="rounded-2xl p-4 flex items-center gap-4 flex-wrap"
             style={{ background: "#FDF2F4", border: `1px solid #F3C2CC` }}>
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-semibold" style={{ color: A.danger }}>
              {legacyLessons.length} lezioni del vecchio sistema ancora presenti
            </p>
            <p className="text-xs mt-0.5" style={{ color: A.muted }}>
              Sono le vecchie lezioni generate automaticamente. Eliminale per lasciare
              solo le lezioni create manualmente con il nuovo sistema a blocchi.
              L'azione è definitiva e non tocca utenti, membership o pagamenti.
            </p>
          </div>
          <button onClick={() => {
                    if (window.confirm(`Eliminare definitivamente le ${legacyLessons.length} vecchie lezioni?`)) purgeLegacy();
                  }}
                  disabled={busy}
                  data-testid="legacy-purge-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: A.danger }}>
            <Trash2 className="h-4 w-4" /> Elimina lezioni legacy
          </button>
        </div>
      ) : null}

      {creatingModule ? (
        <ModuleForm onSave={createModule} onCancel={() => setCreatingModule(false)} saving={busy} />
      ) : null}

      {sorted.length === 0 ? (
        <div className="rounded-2xl p-12 text-center" style={{ background: A.card, border: `2px dashed ${A.border}` }}>
          <GraduationCap className="h-9 w-9 mx-auto mb-3" style={{ color: A.primary }} />
          <p className="text-sm" style={{ color: A.text }}>Nessun modulo. Creane uno per iniziare.</p>
        </div>
      ) : null}

      {sorted.map((m, mi) => {
        const mLessons = lessonsFor(m.module_id);
        return (
          <section key={m.module_id} data-testid={`module-section-${m.module_id}`}
                   className="rounded-2xl overflow-hidden"
                   style={{ background: A.card, border: `1px solid ${A.border}` }}>
            <div className="flex items-center gap-3 px-5 py-4 flex-wrap"
                 style={{ background: "#FBFAFF", borderBottom: `1px solid ${A.border}` }}>
              <div className="flex-1 min-w-[180px]">
                <p className="font-serif text-lg leading-tight" style={{ color: A.text }}>
                  {m.title_it}
                </p>
                <p className="text-xs mt-0.5" style={{ color: A.muted }}>
                  {mLessons.length} lezioni
                  {m.description_it ? ` · ${m.description_it}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => moveModule(mi, -1)} disabled={mi === 0}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5 disabled:opacity-30"
                        style={{ color: A.muted }} title="Sposta su">
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button onClick={() => moveModule(mi, 1)} disabled={mi === sorted.length - 1}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5 disabled:opacity-30"
                        style={{ color: A.muted }} title="Sposta giù">
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button onClick={() => { setEditingModule(editingModule === m.module_id ? null : m.module_id); setCreatingModule(false); }}
                        data-testid={`module-edit-${m.module_id}`}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5"
                        style={{ color: A.primary }} title="Modifica modulo">
                  {editingModule === m.module_id ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                </button>
                <button onClick={() => setDeleteModule(m)}
                        data-testid={`module-delete-${m.module_id}`}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5"
                        style={{ color: A.danger }} title="Elimina modulo">
                  <Trash2 className="h-4 w-4" />
                </button>
                <button onClick={() => createLesson(m.module_id)} disabled={busy}
                        data-testid={`create-lesson-${m.module_id}`}
                        className="ml-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white"
                        style={{ background: A.success }}>
                  <Plus className="h-3.5 w-3.5" /> Nuova lezione
                </button>
              </div>
            </div>

            {editingModule === m.module_id ? (
              <div className="p-4" style={{ borderBottom: `1px solid ${A.border}` }}>
                <ModuleForm initial={m} saving={busy}
                            onSave={(form) => saveModule(m.module_id, form)}
                            onCancel={() => setEditingModule(null)} />
              </div>
            ) : null}

            {mLessons.length === 0 ? (
              <p className="text-sm px-5 py-6 text-center" style={{ color: A.muted }}>
                Nessuna lezione in questo modulo.
              </p>
            ) : (
              <ul>
                {mLessons.map((l, li) => (
                  <li key={l.lesson_id} data-testid={`lesson-row-${l.lesson_id}`}
                      className="flex items-center gap-3 px-5 py-3 flex-wrap"
                      style={{ borderTop: li > 0 ? `1px solid ${A.border}` : "none" }}>
                    <span className="text-xs font-semibold tabular-nums w-8 shrink-0"
                          style={{ color: A.muted }}>
                      {l.lesson_number ?? li + 1}
                    </span>
                    <div className="flex-1 min-w-[160px]">
                      <Link to={`/admin/vip/lessons/${l.lesson_id}`}
                            className="text-sm font-medium hover:underline" style={{ color: A.text }}>
                        {l.title_it || "Senza titolo"}
                      </Link>
                      {l.subtitle ? (
                        <p className="text-xs" style={{ color: A.muted }}>{l.subtitle}</p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {l.published === false ? <Badge kind="warn">Bozza</Badge> : <Badge kind="success">Pubblicata</Badge>}
                      {l.is_free_preview ? <Badge kind="gold">Gratuita</Badge> : null}
                    </div>
                    <div className="flex items-center shrink-0">
                      <button onClick={() => moveLesson(m.module_id, li, -1)} disabled={li === 0}
                              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5 disabled:opacity-30"
                              style={{ color: A.muted }} title="Su">
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button onClick={() => moveLesson(m.module_id, li, 1)} disabled={li === mLessons.length - 1}
                              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5 disabled:opacity-30"
                              style={{ color: A.muted }} title="Giù">
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      <button onClick={() => togglePublish(l)}
                              data-testid={`lesson-publish-${l.lesson_id}`}
                              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5"
                              style={{ color: A.muted }}
                              title={l.published === false ? "Pubblica" : "Metti in bozza"}>
                        {l.published === false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button onClick={() => toggleFree(l)}
                              data-testid={`lesson-free-${l.lesson_id}`}
                              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5"
                              style={{ color: l.is_free_preview ? A.gold : A.muted }}
                              title="Anteprima gratuita on/off">
                        <Gift className="h-4 w-4" />
                      </button>
                      <button onClick={() => duplicateLesson(l.lesson_id)}
                              data-testid={`lesson-duplicate-${l.lesson_id}`}
                              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5"
                              style={{ color: A.muted }} title="Duplica">
                        <Copy className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDeleteLesson(l)}
                              data-testid={`lesson-delete-${l.lesson_id}`}
                              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5"
                              style={{ color: A.danger }} title="Elimina">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <Link to={`/admin/vip/lessons/${l.lesson_id}`}
                            data-testid={`lesson-edit-${l.lesson_id}`}
                            className="ml-1.5 inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg"
                            style={{ background: A.primarySoft, color: A.primary }}>
                        <Pencil className="h-3 w-3" /> Modifica
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {/* Confirm modals */}
      {deleteModule ? (
        <ConfirmModal
          title="Eliminare il modulo?"
          message={`"${deleteModule.title_it}" e tutte le sue lezioni (${lessonsFor(deleteModule.module_id).length}) verranno eliminate definitivamente.`}
          busy={busy} onCancel={() => setDeleteModule(null)} onConfirm={confirmDeleteModule} />
      ) : null}
      {deleteLesson ? (
        <ConfirmModal
          title="Eliminare la lezione?"
          message={`"${deleteLesson.title_it}" verrà eliminata definitivamente.`}
          busy={busy} onCancel={() => setDeleteLesson(null)} onConfirm={confirmDeleteLesson} />
      ) : null}
    </div>
  );
}

function ConfirmModal({ title, message, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4"
         style={{ background: "rgba(20,12,40,0.5)" }} data-testid="confirm-modal">
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: "white" }}>
        <h3 className="font-serif text-xl mb-2" style={{ color: A.text }}>{title}</h3>
        <p className="text-sm mb-5 leading-relaxed" style={{ color: A.muted }}>{message}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm"
                  style={{ border: `1px solid ${A.border}`, color: A.muted }}>
            Annulla
          </button>
          <button onClick={onConfirm} disabled={busy} data-testid="confirm-modal-yes"
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: A.danger }}>
            {busy ? "Eliminazione…" : "Elimina definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}
